/**
 * O documento acadêmico em `.docx` — a versão editável no Word.
 *
 * O PDF é o papel: quem manda é o navegador (a impressão) e nada ali é editável.
 * O `.docx` é o **texto de volta para as mãos de quem escreve**: títulos viram
 * estilos de título, parágrafos viram parágrafos, listas viram listas e tabelas
 * viram tabelas de verdade — para corrigir, mandar para o orientador ou continuar
 * no Word sem digitar tudo de novo.
 *
 * A conversão é do mesmo `DocumentoAcademico` que desenha o papel, então a ordem
 * e o conteúdo batem com a impressão: capa, rosto, resumo, as seções com os
 * blocos e as referências no fim.
 */

import {
  ROTULO_TIPO_DE_TRABALHO,
  blocosDaSecao,
  geometriaDoDocumento,
  linhaDaReferencia,
  listaDaNorma,
  normaDoDocumento,
  numerarSecoes,
  numerosDasFiguras,
  posicaoDaLegenda,
  tabelaRetangular,
  temConteudoImpresso,
} from '@miolos/core';
import type { BlocoDeTexto, DocumentoAcademico } from '@miolos/core';
import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  ImageRun,
  LeaderType,
  Packer,
  PageBreak,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TabStopType,
  TextRun,
  WidthType,
} from 'docx';

/* ------------------------------------------------------------------ */
/* Texto com as marcas do editor (`**negrito**`, `*itálico*`)          */
/* ------------------------------------------------------------------ */

/** As marcas que o editor grava: `**negrito**` e `*itálico*`. */
const MARCA = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;

/** Um trecho vira os `TextRun`s correspondentes (negrito, itálico e texto puro). */
function runsDeTrecho(texto: string, forcarNegrito = false): TextRun[] {
  const runs: TextRun[] = [];
  let fim = 0;
  for (const achado of texto.matchAll(MARCA)) {
    const inicio = achado.index;
    if (inicio > fim) runs.push(new TextRun({ text: texto.slice(fim, inicio), bold: forcarNegrito }));
    const marcado = achado[0];
    if (marcado.startsWith('**')) {
      runs.push(new TextRun({ text: marcado.slice(2, -2), bold: true }));
    } else {
      runs.push(new TextRun({ text: marcado.slice(1, -1), italics: true, bold: forcarNegrito }));
    }
    fim = inicio + marcado.length;
  }
  if (fim < texto.length) runs.push(new TextRun({ text: texto.slice(fim), bold: forcarNegrito }));
  return runs;
}

/** O texto em runs, com a quebra de linha simples preservada. */
function runsDeTexto(texto: string, forcarNegrito = false): TextRun[] {
  const linhas = texto.split('\n');
  const runs: TextRun[] = [];
  linhas.forEach((linha, indice) => {
    if (indice > 0) runs.push(new TextRun({ break: 1 }));
    runs.push(...runsDeTrecho(linha, forcarNegrito));
  });
  return runs;
}

/** Um texto separado por linha em branco vira parágrafos. */
function paragrafosDe(texto: string): string[] {
  return texto
    .split(/\n{2,}/)
    .map((parte) => parte.trim())
    .filter(Boolean);
}

/** Um parágrafo com o texto e o alinhamento pedidos. */
function paragrafo(
  texto: string,
  opcoes: { central?: boolean; reduzido?: boolean; negrito?: boolean } = {},
): Paragraph {
  return new Paragraph({
    alignment: opcoes.central ? AlignmentType.CENTER : AlignmentType.JUSTIFIED,
    spacing: { after: 120 },
    children: runsDeTexto(texto, opcoes.negrito),
  });
}

/* ------------------------------------------------------------------ */
/* Blocos                                                              */
/* ------------------------------------------------------------------ */

/** O nível da seção vira o estilo de título do Word. */
const NIVEL_PARA_TITULO: Record<number, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  1: HeadingLevel.HEADING_1,
  2: HeadingLevel.HEADING_2,
  3: HeadingLevel.HEADING_3,
  4: HeadingLevel.HEADING_4,
  5: HeadingLevel.HEADING_5,
  6: HeadingLevel.HEADING_6,
};

/** Um bloco de texto vira os parágrafos (ou a tabela) correspondentes. */
function paragrafosDoBloco(bloco: BlocoDeTexto): Paragraph[] {
  if (bloco.tipo === 'NOTA') return [];

  if (bloco.tipo === 'TABELA') {
    const linhas = tabelaRetangular(bloco.linhas ?? []);
    if (linhas.length === 0) return [];
    const linhasDeVolta: Paragraph[] = [];
    if (bloco.legenda?.trim()) {
      linhasDeVolta.push(new Paragraph({ children: runsDeTexto(`Tabela — ${bloco.legenda.trim()}`) }));
    }
    return linhasDeVolta;
  }

  if (bloco.tipo === 'LISTA') {
    const itens = (bloco.itens ?? []).map((item) => item.trim()).filter(Boolean);
    const numerada = bloco.lista === 'NUMERADA';
    return itens.map(
      (item, indice) =>
        new Paragraph({
          bullet: numerada ? undefined : { level: 0 },
          numbering: numerada ? { reference: 'lista-numerada', level: 0 } : undefined,
          children: runsDeTexto(item),
          indent: { left: 720 },
        }),
    );
  }

  const texto = bloco.texto ?? '';
  if (!texto.trim()) return [];
  return paragrafosDe(texto).map((parte) => paragrafo(parte));
}

/* ------------------------------------------------------------------ */
/* O documento                                                         */
/* ------------------------------------------------------------------ */

/** O cabeçalho da capa ou da folha de rosto: uma linha centrada e forte. */
function linhaCentral(texto: string, negrito = false): Paragraph {
  return new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 120 },
    children: runsDeTrecho(texto, negrito),
  });
}

/** Quebra para a folha seguinte. */
function novaFolha(): Paragraph {
  return new Paragraph({ children: [new PageBreak()] });
}

/** As peças do documento, na ordem em que saem no papel. */
function filhosDoDocumento(
  documento: DocumentoAcademico,
  imagens: Map<string, ImageRun>,
  paginas?: Map<string, number>,
): (Paragraph | Table)[] {
  const filhos: (Paragraph | Table)[] = [];
  const mapaDasFiguras = numerosDasFiguras(documento);
  /** O manual do Centro Paula Souza põe o título da figura **abaixo** da imagem. */
  const legendaAbaixo = posicaoDaLegenda(documento) === 'ABAIXO';
  const { identificacao } = documento;
  const autores = identificacao.autores.map((autor) => autor.nome.trim()).filter(Boolean);
  const tituloCompleto = [identificacao.titulo, identificacao.subtitulo].filter(Boolean).join(': ');
  const tem = (item: string) => documento.itens.includes(item as never);

  /* ---------------- CAPA ---------------- */
  if (tem('CAPA')) {
    if (identificacao.instituicao) filhos.push(linhaCentral(identificacao.instituicao.toUpperCase(), true));
    if (identificacao.unidade) filhos.push(linhaCentral(identificacao.unidade));
    if (identificacao.curso) filhos.push(linhaCentral(identificacao.curso));
    filhos.push(new Paragraph({ spacing: { before: 400, after: 120 }, alignment: AlignmentType.CENTER, children: runsDeTrecho(autores.join('; ') || 'AUTOR') }));
    filhos.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 120 },
        children: runsDeTrecho(tituloCompleto || 'TÍTULO DO TRABALHO', true),
      }),
    );
    filhos.push(new Paragraph({ spacing: { before: 400 }, alignment: AlignmentType.CENTER, children: runsDeTrecho(identificacao.cidade || 'CIDADE') }));
    filhos.push(linhaCentral(identificacao.ano || ''));
    filhos.push(novaFolha());
  }

  /* ---------------- FOLHA DE ROSTO ---------------- */
  if (tem('FOLHA_DE_ROSTO')) {
    filhos.push(linhaCentral(autores.join('; ') || 'AUTOR', true));
    filhos.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 120 },
        children: runsDeTrecho(tituloCompleto || 'TÍTULO DO TRABALHO', true),
      }),
    );
    const tipo = identificacao.tipo ? ROTULO_TIPO_DE_TRABALHO[identificacao.tipo] : 'Trabalho';
    const nota = [
      `${tipo} apresentado`,
      identificacao.curso ? `ao curso de ${identificacao.curso}` : '',
      identificacao.instituicao ? `da ${identificacao.instituicao}` : '',
      'como requisito parcial',
      identificacao.orientador ? `, sob orientação de ${identificacao.orientador}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    if (nota) {
      filhos.push(
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          indent: { left: 3600 },
          children: runsDeTexto(`${nota}.`),
        }),
      );
    }
    filhos.push(new Paragraph({ spacing: { before: 400 }, alignment: AlignmentType.CENTER, children: runsDeTrecho(identificacao.cidade || 'CIDADE') }));
    filhos.push(linhaCentral(identificacao.ano || ''));
    filhos.push(novaFolha());
  }

  /* ---------------- FOLHA DE APROVAÇÃO ---------------- */
  if (tem('FOLHA_DE_APROVACAO')) {
    filhos.push(linhaCentral(autores.join('; ') || 'AUTOR', true));
    filhos.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 300, after: 120 },
        children: runsDeTrecho(tituloCompleto || 'TÍTULO DO TRABALHO', true),
      }),
    );
    filhos.push(new Paragraph({ spacing: { before: 300 }, children: runsDeTexto('Aprovado em: ____ / ____ / ________') }));
    (identificacao.banca ?? '')
      .split('\n')
      .filter(Boolean)
      .forEach((linha) => {
        filhos.push(new Paragraph({ spacing: { before: 200 }, alignment: AlignmentType.CENTER, children: runsDeTexto(`_______________________________________\n${linha}`) }));
      });
    filhos.push(novaFolha());
  }

  /* ---------------- PEÇAS DE ABERTURA ---------------- */
  const pecas: Array<[string | undefined, string]> = [
    [documento.dedicatoria, 'Dedicatória'],
    [documento.agradecimentos, 'Agradecimentos'],
    [documento.epigrafe, 'Epígrafe'],
  ];
  for (const [texto, titulo] of pecas) {
    if (!texto?.trim()) continue;
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, children: runsDeTrecho(titulo) }));
    for (const parte of paragrafosDe(texto)) filhos.push(paragrafo(parte));
    filhos.push(novaFolha());
  }

  /* ---------------- RESUMO E ABSTRACT ---------------- */
  if (documento.resumo.trim()) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, children: runsDeTrecho('Resumo') }));
    for (const parte of paragrafosDe(documento.resumo)) filhos.push(paragrafo(parte));
    if (documento.palavrasChave.length > 0) {
      filhos.push(new Paragraph({ children: [new TextRun({ text: 'Palavras-chave: ', bold: true }), ...runsDeTrecho(documento.palavrasChave.join('; ') + '.')] }));
    }
  }
  if (documento.abstract.trim()) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, children: runsDeTrecho('Abstract') }));
    for (const parte of paragrafosDe(documento.abstract)) filhos.push(paragrafo(parte));
    if (documento.keywords.length > 0) {
      filhos.push(new Paragraph({ children: [new TextRun({ text: 'Keywords: ', bold: true }), ...runsDeTrecho(documento.keywords.join('; ') + '.')] }));
    }
  }

  /* ---------------- RESUMOS EM OUTROS IDIOMAS ---------------- */
  for (const item of documento.resumos ?? []) {
    if (!item.resumo.trim()) continue;
    filhos.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        children: runsDeTrecho(item.titulo.trim() || 'Resumo'),
      }),
    );
    for (const parte of paragrafosDe(item.resumo)) filhos.push(paragrafo(parte));
    if (item.palavrasChave.length > 0) {
      filhos.push(
        new Paragraph({
          children: [
            new TextRun({ text: 'Palavras-chave: ', bold: true }),
            ...runsDeTrecho(`${item.palavrasChave.join('; ')}.`),
          ],
        }),
      );
    }
  }

  /* ---------------- LISTAS ---------------- */
  if (tem('LISTA_DE_FIGURAS') && documento.figuras.length > 0) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, children: runsDeTrecho('Lista de figuras') }));
    documento.figuras.forEach((figura, indice) => {
      const numero = mapaDasFiguras.get(figura.id) ?? indice + 1;
      filhos.push(new Paragraph({ children: runsDeTexto(`Figura ${numero} — ${figura.legenda}`) }));
    });
  }
  if (tem('LISTA_DE_ABREVIATURAS') && documento.abreviaturas.length > 0) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, children: runsDeTrecho('Lista de abreviaturas e siglas') }));
    [...documento.abreviaturas].sort().forEach((linha) => filhos.push(new Paragraph({ children: runsDeTexto(linha) })));
  }

  /* ---------------- SUMÁRIO ---------------- */
  if (tem('SUMARIO')) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, alignment: AlignmentType.CENTER, children: runsDeTrecho('Sumário') }));
    const comCapitulos = documento.modelo === 'LIVRO';
    const secoes = numerarSecoes(documento.secoes, comCapitulos ? { livro: true } : {}).filter(
      (_, indice) => temConteudoImpresso(documento.secoes, indice),
    );
    for (const item of secoes) {
      const numeroDaPagina = paginas?.get(item.secao.id);
      filhos.push(
        new Paragraph({
          indent: { left: (item.nivel - 1) * 360 },
          tabStops: [
            {
              type: TabStopType.RIGHT,
              position: 9026,
              leader: numeroDaPagina !== undefined ? LeaderType.DOT : undefined,
            },
          ],
          children: [
            new TextRun({ text: item.numero ? `${item.numero} ` : '', bold: item.nivel === 1 }),
            ...runsDeTrecho(item.titulo),
            ...(numeroDaPagina !== undefined
              ? [new TextRun({ text: `\t${numeroDaPagina}` })]
              : []),
          ],
        }),
      );
    }
    filhos.push(novaFolha());
  }

  /* ---------------- TEXTO ---------------- */
  const comCapitulos = documento.modelo === 'LIVRO';
  const secoes = numerarSecoes(documento.secoes, comCapitulos ? { livro: true } : {}).filter(
    (_, indice) => temConteudoImpresso(documento.secoes, indice),
  );

  for (const item of secoes) {
    const nivel = NIVEL_PARA_TITULO[item.nivel] ?? HeadingLevel.HEADING_1;
    if (item.secao.titulo.trim()) {
      filhos.push(
        new Paragraph({
          heading: nivel,
          pageBreakBefore: item.nivel === 1,
          children: [new TextRun({ text: item.numero ? `${item.numero} ` : '' }), ...runsDeTrecho(item.titulo)],
        }),
      );
    }

    for (const bloco of blocosDaSecao(item.secao)) {
      if (bloco.tipo === 'TABELA') {
        filhos.push(...paragrafosDoBloco(bloco));
        filhos.push(tabelaEmDocx(bloco));
        if (bloco.fonte?.trim()) filhos.push(new Paragraph({ children: runsDeTexto(`Fonte: ${bloco.fonte.trim()}`) }));
      } else if (bloco.tipo === 'IMAGEM') {
        /*
         * A figura entra como imagem de verdade, não como espaço reservado. A
         * legenda sai acima ou abaixo conforme o padrão da instituição.
         */
        const legenda = bloco.legenda?.trim()
          ? new Paragraph({ children: runsDeTexto(`Figura ${mapaDasFiguras.get(bloco.id) ?? 0} — ${bloco.legenda.trim()}`) })
          : null;
        const run = imagens.get((bloco.url ?? '').trim());
        const desenho = run ? new Paragraph({ alignment: AlignmentType.CENTER, children: [run] }) : null;

        if (legendaAbaixo) {
          if (desenho) filhos.push(desenho);
          if (legenda) filhos.push(legenda);
        } else {
          if (legenda) filhos.push(legenda);
          if (desenho) filhos.push(desenho);
        }
        if (bloco.fonte?.trim()) filhos.push(new Paragraph({ children: runsDeTexto(`Fonte: ${bloco.fonte.trim()}`) }));
      } else {
        filhos.push(...paragrafosDoBloco(bloco));
      }
    }
  }

  /* ---------------- FIGURAS (junto do texto) ---------------- */
  for (const figura of documento.figuras) {
    const numero = mapaDasFiguras.get(figura.id) ?? 0;
    const legenda = figura.legenda?.trim()
      ? new Paragraph({ children: runsDeTexto(`Figura ${numero} — ${figura.legenda.trim()}`) })
      : null;
    const run = imagens.get((figura.url ?? '').trim());
    const desenho = run ? new Paragraph({ alignment: AlignmentType.CENTER, children: [run] }) : null;

    if (legendaAbaixo) {
      if (desenho) filhos.push(desenho);
      if (legenda) filhos.push(legenda);
    } else {
      if (legenda) filhos.push(legenda);
      if (desenho) filhos.push(desenho);
    }
    if (figura.fonte?.trim()) filhos.push(new Paragraph({ children: runsDeTexto(`Fonte: ${figura.fonte.trim()}`) }));
  }

  /* ---------------- REFERÊNCIAS ---------------- */
  if (documento.referencias.length > 0) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, pageBreakBefore: true, alignment: AlignmentType.CENTER, children: runsDeTrecho('Referências') }));
    const norma = normaDoDocumento(documento);
    for (const item of listaDaNorma(documento.referencias, norma)) {
      const linha = linhaDaReferencia(item.referencia, norma, item.numero).trim();
      if (linha) filhos.push(new Paragraph({ children: runsDeTrecho(linha) }));
    }
  }

  /* ---------------- ANEXOS ---------------- */
  if ((documento.anexos ?? []).length > 0) {
    filhos.push(new Paragraph({ heading: HeadingLevel.HEADING_1, pageBreakBefore: true, alignment: AlignmentType.CENTER, children: runsDeTrecho('Anexos') }));
    documento.anexos!.forEach((anexo, indice) => {
      filhos.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          pageBreakBefore: true,
          children: runsDeTrecho(`Anexo ${String.fromCharCode(65 + indice)}`),
        }),
      );
      filhos.push(new Paragraph({ alignment: AlignmentType.CENTER, children: runsDeTrecho(anexo.nome) }));
    });
  }

  return filhos;
}

/** A tabela em elementos do Word. */
function tabelaEmDocx(bloco: BlocoDeTexto): Table {
  const linhas = tabelaRetangular(bloco.linhas ?? []);
  const [cabecalho, ...corpo] = linhas;
  const borda = { style: BorderStyle.SINGLE, size: 4, color: '000000' };
  const celula = (texto: string, forte: boolean) =>
    new TableCell({
      borders: { top: borda, bottom: borda, left: borda, right: borda },
      children: [new Paragraph({ children: runsDeTrecho(texto, forte) })],
    });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      ...(cabecalho ? [new TableRow({ tableHeader: true, children: cabecalho.map((c) => celula(c, true)) })] : []),
      ...corpo.map((linha) => new TableRow({ children: linha.map((c) => celula(c, false)) })),
    ],
  });
}

/** O nome do arquivo, tirado do título ou do nome salvo. */
export function nomeDoArquivoDocx(documento: DocumentoAcademico, nomeSalvo: string): string {
  const base =
    nomeSalvo.trim() ||
    documento.identificacao.titulo.trim() ||
    'documento';
  const semExtensao = base.replace(/\.(docx|pdf)$/i, '').trim();
  return `${semExtensao || 'documento'}.docx`;
}

/** O tipo da imagem no formato do `docx`, tirado do `Content-Type` da resposta. */
function tipoDaImagem(tipo: string): 'png' | 'jpg' | 'gif' | 'bmp' {
  if (tipo.includes('jpeg') || tipo.includes('jpg')) return 'jpg';
  if (tipo.includes('gif')) return 'gif';
  if (tipo.includes('bmp')) return 'bmp';
  return 'png';
}

/** Baixa a imagem e a devolve pronta para entrar no Word, na largura pedida. */
async function imagemEmRun(url: string, larguraMm: number): Promise<ImageRun | null> {
  try {
    const resposta = await fetch(url);
    if (!resposta.ok) return null;
    const blob = await resposta.blob();
    const bitmap = await createImageBitmap(blob);
    const pxPorMm = 96 / 25.4;
    const larguraPx = Math.max(1, Math.round(larguraMm * pxPorMm));
    const alturaPx = Math.max(1, Math.round(larguraPx * (bitmap.height / bitmap.width)));
    const dados = new Uint8Array(await blob.arrayBuffer());
    return new ImageRun({
      type: tipoDaImagem(resposta.headers.get('content-type') ?? ''),
      data: dados,
      transformation: { width: larguraPx, height: alturaPx },
    });
  } catch {
    return null;
  }
}

/** Gera o `.docx` e começa o download no navegador. */
export async function baixarDocumentoDocx(
  documento: DocumentoAcademico,
  nomeDoArquivo: string,
  resolverImagem: (url: string) => string = (url) => url,
  paginas?: Map<string, number>,
): Promise<void> {
  /*
   * As figuras entram como imagens de verdade. Baixa-se tudo antes de montar o
   * documento: a montagem é síncrona, e uma figura que falhou simplesmente não
   * entra (não derruba o arquivo inteiro).
   */
  const imagens = new Map<string, ImageRun>();
  const promessas: Promise<unknown>[] = [];
  for (const secao of documento.secoes) {
    for (const bloco of blocosDaSecao(secao)) {
      if (bloco.tipo === 'IMAGEM' && bloco.url?.trim()) {
        promessas.push(
          imagemEmRun(resolverImagem(bloco.url.trim()), bloco.larguraMm ?? 140).then((run) => {
            if (run) imagens.set(bloco.url!.trim(), run);
          }),
        );
      }
    }
  }
  for (const figura of documento.figuras) {
    if (figura.url?.trim()) {
      promessas.push(
        imagemEmRun(resolverImagem(figura.url.trim()), figura.larguraMm).then((run) => {
          if (run) imagens.set(figura.url!.trim(), run);
        }),
      );
    }
  }
  await Promise.all(promessas);

  const geometria = geometriaDoDocumento(documento);
  const corpoHalf = Math.round(geometria.corpoPt * 2);
  const tituloHalf = Math.round((documento.tamanhoDoTituloPt ?? geometria.corpoPt + 2) * 2);

  const estiloDeTitulo = (tamanho: number) => ({
    run: { color: '000000', bold: true, size: tamanho, font: 'Times New Roman' },
    paragraph: { spacing: { before: 240, after: 120 } },
  });

  const doc = new Document({
    creator: 'Miolix',
    title: documento.identificacao.titulo || 'Documento',
    styles: {
      default: {
        document: { run: { font: 'Times New Roman', size: corpoHalf, color: '000000' } },
        /* Título do Word nasce azul; a norma quer tudo preto. */
        heading1: estiloDeTitulo(tituloHalf),
        heading2: estiloDeTitulo(corpoHalf),
        heading3: estiloDeTitulo(corpoHalf),
        heading4: estiloDeTitulo(corpoHalf),
        heading5: estiloDeTitulo(corpoHalf),
        heading6: estiloDeTitulo(corpoHalf),
      },
    },
    numbering: {
      config: [
        {
          reference: 'lista-numerada',
          levels: [
            { level: 0, format: 'decimal', text: '%1.', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 720, hanging: 360 } } } },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {},
        children: filhosDoDocumento(documento, imagens, paginas),
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const ligacao = document.createElement('a');
  ligacao.href = url;
  ligacao.download = nomeDoArquivo;
  document.body.appendChild(ligacao);
  ligacao.click();
  ligacao.remove();
  URL.revokeObjectURL(url);
}
